import {
  createAction,
  createSlice,
  PayloadAction,
  createSelector
} from '@reduxjs/toolkit'
import {
  VisitStatusEnum,
  WorkerDepartmentEnum
} from '__generated__/globalTypes'
import { EventType, TimeSlotType } from 'components/calendar/Calendar.screen'
import { CleanerData } from 'ts/types/Cleaner'
import { formatDate } from 'utils/date'

import { RootState } from '../../redux/slices'
import { mapCleanerToFreeSlots } from './lib'

export type SelectedCleaner = {
  id: string
  name: string
  workerRating: number
  scheduleVariantId?: string | null
  avatarUrl?: string | null
  department: WorkerDepartmentEnum
}

const initialVisitStatusFilter: Partial<{ [key in VisitStatusEnum]: boolean }> =
  {
    [VisitStatusEnum.SCHEDULED]: true,
    [VisitStatusEnum.EXPIRED]: true,
    [VisitStatusEnum.COMPLETED]: true,
    [VisitStatusEnum.CANCELLED_BY_CLIENT]: false,
    [VisitStatusEnum.CANCELLED_BY_OPS]: false,
    [VisitStatusEnum.CANCELLED_BY_WORKER]: false,
    [VisitStatusEnum.MISSED_BY_WORKER]: false,
    [VisitStatusEnum.PENDING_CLIENT_SCHEDULE]: false,
    [VisitStatusEnum.UNABLE_TO_SCHEDULE]: false
  }

export type SchedulerVisitStatusFilter = typeof initialVisitStatusFilter

export type FreeSlotData = EventType & TimeSlotType
export type FreeSlotCleaners = Record<string, FreeSlotData[]>

type State = {
  queriedCleaners: CleanerData[]
  selectedCleaner: SelectedCleaner | null
  freeslotCleaners: FreeSlotCleaners
  clearSearch: boolean
  loading: boolean
  visitStatusFilter: SchedulerVisitStatusFilter
  expandView: boolean
  currentDate: string
  queriedCleanerLimit: number
  refreshScheduler: boolean
  workerPerList: number
  section: number
  selectedTab: number
  totalWorkerList: number
}

const INREMENT_QUERIED_CLEANER_LIMIT = 10
export const INITIAL_QUERIED_CLEANER_LIMIT = 10

export const initialState: State = {
  queriedCleaners: [],
  selectedCleaner: null,
  freeslotCleaners: {},
  clearSearch: false,
  loading: false,
  visitStatusFilter: initialVisitStatusFilter,
  expandView: true,
  currentDate: formatDate(new Date()),
  queriedCleanerLimit: INITIAL_QUERIED_CLEANER_LIMIT,
  refreshScheduler: false,
  workerPerList: 30,
  totalWorkerList: 0,
  section: 0,
  selectedTab: 0
}

type SetQueriedCleaners = {
  cleaners: CleanerData[]
}

type AddQueriedCleanerToFront = {
  cleaner: CleanerData
}

type Loading = {
  loading: boolean
}

type WorkerSchedule = {
  id: string
  name: string
  workerRating: number
  scheduleVariantId?: string | null
  avatarUrl?: string | null
  department?: WorkerDepartmentEnum
}

export const setVisitStatusFilterAction =
  createAction<SchedulerVisitStatusFilter>(
    'schedulerCleaners/setVisitStatusFilter'
  )

export const setQueriedCleanersAction = createAction<SetQueriedCleaners>(
  'schedulerCleaners/setQueriedCleaners'
)

export const addQueriedCleanerToFrontAction =
  createAction<AddQueriedCleanerToFront>(
    'schedulerCleaners/addQueriedCleanerToFront'
  )

const schedulerCleanersSlice = createSlice({
  name: 'schedulerCleaners',
  initialState,
  reducers: {
    selectCleaner(state, action: PayloadAction<WorkerSchedule>) {
      const {
        id,
        name,
        workerRating,
        scheduleVariantId,
        avatarUrl,
        department
      } = action.payload
      state.selectedCleaner = {
        id,
        name,
        workerRating,
        scheduleVariantId,
        avatarUrl,
        department: department ?? WorkerDepartmentEnum.HOME_CLEANING
      }

      // set the current date to first combination to focus calendar
      if (scheduleVariantId) {
        const [firstCombination] = state.freeslotCleaners[scheduleVariantId]
        state.currentDate = formatDate(new Date(firstCombination.startDate))
      }
    },
    clearSelectedCleaner(state) {
      state.selectedCleaner = null
    },
    clearCleaners(state) {
      state.queriedCleaners = []
      state.queriedCleanerLimit = INITIAL_QUERIED_CLEANER_LIMIT
      state.selectedCleaner = null
      state.section = 0
      state.workerPerList = 30
      state.totalWorkerList = 0
    },
    setClearSearch(
      state,
      { payload: { action } }: { payload: { action: boolean } }
    ) {
      state.clearSearch = action
    },
    setQueriedCleaners(state, action: PayloadAction<SetQueriedCleaners>) {
      const { cleaners } = action.payload
      state.freeslotCleaners = cleaners.reduce<FreeSlotCleaners>(
        (allFreeslots, cleaner) => {
          if (cleaner.scheduleVariantId) {
            allFreeslots[cleaner.scheduleVariantId] =
              mapCleanerToFreeSlots(cleaner)
          }
          return allFreeslots
        },
        {}
      )

      state.queriedCleaners = cleaners.map((cleaner) => {
        delete cleaner.combinations
        return cleaner
      })
    },
    addQueriedCleanerToFront(
      state,
      action: PayloadAction<AddQueriedCleanerToFront>
    ) {
      const { cleaner } = action.payload

      if (cleaner.scheduleVariantId) {
        state.freeslotCleaners[cleaner.scheduleVariantId] =
          mapCleanerToFreeSlots(cleaner)
      }

      const cleanerCopy = { ...cleaner }
      delete cleanerCopy.combinations
      state.queriedCleaners.unshift(cleanerCopy)
    },
    setLoading(state, action: PayloadAction<Loading>) {
      const { loading } = action.payload
      state.loading = loading
    },
    setVisitStatusFilter(
      state,
      { payload: visitStatusFilter }: PayloadAction<SchedulerVisitStatusFilter>
    ) {
      state.visitStatusFilter = visitStatusFilter
    },
    toggleExpandView(state) {
      state.expandView = !state.expandView
    },

    setCurrentDate(
      state,
      { payload: { currentDate } }: { payload: { currentDate: string } }
    ) {
      state.currentDate = currentDate
    },
    incrementQueriedCleanerLimit(state) {
      state.queriedCleanerLimit += INREMENT_QUERIED_CLEANER_LIMIT
    },
    resetQueriedCleanerLimit(state) {
      state.queriedCleanerLimit += INITIAL_QUERIED_CLEANER_LIMIT
    },
    setRefreshScheduler(
      state,
      {
        payload: { refreshScheduler }
      }: PayloadAction<{ refreshScheduler: boolean }>
    ) {
      state.refreshScheduler = refreshScheduler
    },
    setWorkerPerList(state, action: PayloadAction<number>) {
      state.workerPerList = action.payload
    },
    setSection(state, action: PayloadAction<number>) {
      state.section = action.payload
    },
    setSelectedTab(state, action: PayloadAction<number>) {
      state.selectedTab = action.payload
    },
    setTotalWorkerList(state, action: PayloadAction<number>) {
      state.totalWorkerList = action.payload
    }
  }
})

export const selectedCleanerSelector = createSelector(
  (state: RootState) => state.schedulerCleanersReducer.selectedCleaner,
  (selectedCleaner) => selectedCleaner
)

export const clearSearchSelector = (state: RootState): boolean =>
  state.schedulerCleanersReducer.clearSearch

export const queriedCleanerLimitSelector = createSelector(
  (state: RootState) => state.schedulerCleanersReducer.queriedCleanerLimit,
  (queriedCleanerLimit) => queriedCleanerLimit
)

export const queriedCleanersSelector = createSelector(
  [
    (state: RootState) => state.schedulerCleanersReducer.queriedCleaners,
    queriedCleanerLimitSelector
  ],
  (queriedCleaners, queriedCleanerLimit) =>
    queriedCleaners.slice(0, queriedCleanerLimit)
)

export const totalQueriedCleanersSelector = (state: RootState): number =>
  state.schedulerCleanersReducer.queriedCleaners.length

export const schedulerLoadingSelector = (state: RootState): boolean =>
  state.schedulerCleanersReducer.loading

const freeslotCleanersSelector = createSelector(
  (state: RootState) => state.schedulerCleanersReducer.freeslotCleaners,
  (freeslotCleaners) => freeslotCleaners
)

export const allWorkerFreeSlotSelector = createSelector(
  [freeslotCleanersSelector],
  (freeslotCleaners) =>
    Object.values(freeslotCleaners).reduce<FreeSlotData[]>(
      (allEvents, freeslots) => [...allEvents, ...freeslots],
      []
    )
)

export const cleanerFreeSlotsSelector = createSelector(
  [selectedCleanerSelector, freeslotCleanersSelector],
  (selectedCleaner, freeslotCleaners) => {
    if (
      selectedCleaner?.scheduleVariantId &&
      freeslotCleaners[selectedCleaner.scheduleVariantId]
    ) {
      return freeslotCleaners[selectedCleaner.scheduleVariantId]
    }

    return []
  }
)

export const currentDateSelector = createSelector(
  (state: RootState) => state.schedulerCleanersReducer.currentDate,
  (currentDate) => currentDate
)

export const visitStatusFilterSelector = createSelector(
  (state: RootState): SchedulerVisitStatusFilter =>
    state.schedulerCleanersReducer.visitStatusFilter,
  (visitStatusFilter) => visitStatusFilter
)

export const selectedVisitStatusFilterSelector = createSelector(
  [visitStatusFilterSelector],
  (visitStatusFilter) => {
    const selectedStatus = Object.keys(visitStatusFilter).reduce<
      VisitStatusEnum[]
    >((statuses, status) => {
      if (visitStatusFilter[status]) {
        statuses.push(status as VisitStatusEnum)
      }
      return statuses
    }, [])

    return selectedStatus
  }
)

export const expandViewSelector = (state: RootState): boolean =>
  state.schedulerCleanersReducer.expandView

export const refreshSchedulerSelector = (state: RootState) => {
  return state.schedulerCleanersReducer.refreshScheduler
}

export const workerPerListSelector = (state: RootState): number =>
  state.schedulerCleanersReducer.workerPerList

export const sectionSelector = (state: RootState): number =>
  state.schedulerCleanersReducer.section

export const selectedTabSelector = (state: RootState): number =>
  state.schedulerCleanersReducer.selectedTab

export const totalWorkerListSelector = (state: RootState): number =>
  state.schedulerCleanersReducer.totalWorkerList

export const { name, actions, reducer } = schedulerCleanersSlice
